import React, { useEffect, useMemo, useState } from 'react';

import { HockeyLessonData } from '@types';
import { ProgressIndicator } from '_atoms';
import { useAppSelector } from '_hooks';
import {
  BookingSubmissionStatus,
  CustomerInfo as CustomerInfoComponent,
  HockeyLessonConfirmation,
  ParticipantsInfo,
  Payment,
  SelectHockeyLesson,
  WaiverNew,
} from '_molecules';
import { HockeyLessonService } from '_services';
import HelperMethods from '_utils/HelperMethods';

export type HockeyLessonStep =
  | 'selectHockeyLesson'
  | 'participantsInfo'
  | 'customerInfo'
  | 'waiver'
  | 'payment'
  | 'confirmation';

const HockeyLessonFlow: React.FC = () => {
  const ticketBarcode = HelperMethods.generateRandom6DigitNumber();
  const [currentStep, setCurrentStep] =
    useState<HockeyLessonStep>('selectHockeyLesson');
  const [lessonData, setLessonData] = useState<HockeyLessonData>({
    barcodeId: `hl${ticketBarcode.toString()}`,
  });

  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Get authentication state and user data
  const isAuthenticated = useAppSelector(state => state.auth.isAuthenticated);
  const accountDetails = useAppSelector(state => state.auth.accountDetails);
  const currentUser = useAppSelector(state => state.auth.currentUser);

  // Define steps - always include customer info
  const STEPS = useMemo(() => {
    return [
      { id: 'selectHockeyLesson', label: 'Select Lesson' },
      { id: 'participantsInfo', label: 'Participants' },
      { id: 'customerInfo', label: 'Your Information' },
      { id: 'waiver', label: 'Waiver' },
      { id: 'payment', label: 'Payment' },
    ];
  }, []);

  // Populate customer info with authenticated user data
  useEffect(() => {
    if (isAuthenticated && (accountDetails || currentUser)) {
      const userInfo = accountDetails || currentUser;

      console.log('userInfo in hockey lesson flow', userInfo);

      // Only populate if customerInfo hasn't been filled yet
      if (!lessonData.customerInfo) {
        setLessonData(prev => ({
          ...prev,
          customerInfo: {
            id: userInfo.id || userInfo.sub || '',
            firstName: userInfo.firstName || userInfo.givenName || '',
            lastName: userInfo.lastName || userInfo.familyName || '',
            email: userInfo.email || '',
            phoneNumber: userInfo.phoneNumber || userInfo?.phoneNumber || '',
          },
        }));
      }
    }
  }, [isAuthenticated, accountDetails, currentUser, lessonData.customerInfo]);

  const handleNext = (stepData?: Partial<HockeyLessonData>) => {
    if (stepData) {
      setLessonData(prev => ({ ...prev, ...stepData }));
    }

    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex < STEPS.length - 1) {
      const nextStep = STEPS[currentIndex + 1].id as HockeyLessonStep;
      setCurrentStep(nextStep);

      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    const currentIndex = STEPS.findIndex(step => step.id === currentStep);

    if (currentIndex > 0) {
      const previousStep = STEPS[currentIndex - 1].id as HockeyLessonStep;
      setCurrentStep(previousStep);

      // Scroll to top for mobile users
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleStepComplete = (stepData: Partial<HockeyLessonData>) => {
    setLessonData(prev => ({ ...prev, ...stepData }));
  };

  const processPayment = (stepData: Partial<HockeyLessonData>) => {
    const updatedData = { ...lessonData, ...stepData };
    setLessonData(updatedData);

    console.log('Updated hockey lesson data with payment info:', updatedData);

    setCurrentStep('confirmation');
    // Scroll to top for mobile users
    window.scrollTo({ top: 0, behavior: 'smooth' });

    submitLessonBookingDataWithData(updatedData);
  };

  const submitLessonBookingDataWithData = async (data: HockeyLessonData) => {
    if (
      (!data.isSkatingWaiverAttached || !data.attachedWaivers) &&
      (!data.skaterWaivers || data.skaterWaivers.length === 0)
    ) {
      setSubmitError('No waiver data to submit');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Create FormData to send to the API
      const formData = new FormData();

      // Option 1: Send as JSON string (current approach)
      formData.append(
        'bookingData',
        JSON.stringify({
          barcodeId: data.barcodeId,
          selectedLesson: data.selectedLesson,
          participantsInfo: data.participantsInfo,
          customerInfo: data.customerInfo,
          waiverAccepted: data.waiverAccepted,
          skaterWaivers: data.skaterWaivers,
          isSkatingWaiverAttached: data.isSkatingWaiverAttached,
          skaterWaiverAttached: data.skaterWaiverAttached,
          attachedWaivers: data.attachedWaivers,
          paymentInfo: data.paymentInfo,
          submittedAt: new Date().toISOString(),
        }),
      );

      // APPROACH 1: File Objects (Recommended for most cases)
      // Pros:
      // - Smaller payload size (files are compressed)
      // - Better for large files
      // - Standard way to handle file uploads
      // - Browser handles file encoding automatically
      // Cons:
      // - Requires multipart/form-data
      // - Server needs to handle file uploads
      for (const waiver of data.skaterWaivers || []) {
        if (waiver.signedPDF) {
          formData.append(
            'signedPDFs',
            waiver.signedPDF,
            `${waiver.skaterName}_waiver.pdf`,
          );
        }
      }

      // Submit to API using HockeyLessonService (you'll need to create this)
      await HockeyLessonService.createHockeyLessonBooking(formData);

      // For now, simulate a successful response
      console.log('Hockey lesson booking submitted successfully:', formData);

      // Set submitted state and navigate to success step
      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting waiver:', error);
      setSubmitError(
        error?.detail ||
          error?.message ||
          'Failed to submit waiver. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'selectHockeyLesson':
        return (
          <SelectHockeyLesson
            selectedLesson={lessonData.selectedLesson}
            onNext={handleNext}
            onStepComplete={handleStepComplete}
          />
        );
      case 'participantsInfo':
        return (
          <ParticipantsInfo
            participantsInfo={lessonData.participantsInfo}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
            maxAge={lessonData.selectedLesson?.maxAge}
            minAge={lessonData.selectedLesson?.minAge}
          />
        );
      case 'customerInfo':
        return (
          <CustomerInfoComponent
            customerInfo={lessonData.customerInfo}
            onNext={handleNext}
            onPrevious={handlePrevious}
            onStepComplete={handleStepComplete}
          />
        );
      case 'waiver':
        return (
          <WaiverNew
            skatersInfo={lessonData.participantsInfo?.map(p => ({
              ...p,
              email: '',
              phoneNumber: '',
              isMinor: false,
              isAdult: true,
              isChild: false,
            }))}
            onPrevious={handlePrevious}
            isInFlow={true}
            onContinue={waiverData => {
              // Check if ONLY existing waivers were attached (no new form filled)
              if (waiverData.isExistingWaiver && !waiverData.hasMixedWaivers) {
                // Use the attached waivers directly
                handleNext({
                  waiverAccepted: true,
                  isSkatingWaiverAttached: true,
                  attachedWaivers: waiverData.attachedWaivers,
                });
                return;
              }

              // Handle mixed scenario or new waiver only
              const skaterWaivers = [];

              // Add new waiver data if it exists (from filled form)
              if (waiverData.adult && waiverData.signedPDF) {
                // Add adult waiver
                const adultWaiver = {
                  skaterId: '1',
                  skaterName: `${waiverData.adult.firstName} ${waiverData.adult.lastName}`,
                  dateOfBirth: waiverData.adult.dateOfBirth,
                  isAdult: true,
                  hasReadWaiver: waiverData.adult.hasReadWaiver,
                  accepted: waiverData.adult.accepted,
                  signature: waiverData.adult.signature,
                  signedBy: waiverData.adult.signedBy,
                  relationship: 'Self',
                  email: waiverData.adult.email,
                  phoneNumber: waiverData.adult.phoneNumber,
                  emergencyContactPhone: waiverData.adult.emergencyContactPhone,
                  signedPDF: waiverData.signedPDF,
                  barcodeId: waiverData.barcodeId,
                  minors: waiverData.minors.map(
                    (_m: any, idx: number) => `minor-${idx + 2}`,
                  ),
                  waiverId: waiverData.waiverId,
                };
                skaterWaivers.push(adultWaiver);

                // Add minor waivers (they reference the adult's PDF)
                waiverData.minors.forEach((minor: any, idx: number) => {
                  const minorWaiver = {
                    skaterId: `minor-${idx + 2}`,
                    skaterName: `${minor.firstName} ${minor.lastName}`,
                    dateOfBirth: minor.dateOfBirth,
                    isAdult: false,
                    hasReadWaiver: waiverData.adult.hasReadWaiver,
                    accepted: waiverData.adult.accepted,
                    signature: waiverData.adult.signature,
                    signedBy: waiverData.adult.signedBy,
                    relationship: 'Guardian',
                    email: waiverData.adult.email,
                    phoneNumber: waiverData.adult.phoneNumber,
                    emergencyContactPhone:
                      waiverData.adult.emergencyContactPhone,
                    signedPDF: waiverData.signedPDF,
                    barcodeId: waiverData.barcodeId,
                    minors: [],
                    waiverId: waiverData.waiverId,
                  };
                  skaterWaivers.push(minorWaiver);
                });
              }

              handleNext({
                waiverAccepted: true,
                ...(skaterWaivers.length > 0 && { skaterWaivers }),
                isSkatingWaiverAttached:
                  waiverData.attachedWaivers !== null &&
                  waiverData.attachedWaivers !== undefined,
                attachedWaivers: waiverData.attachedWaivers,
              });
            }}
          />
        );
      case 'payment':
        return (
          <Payment
            paymentType="hockey"
            bookingData={lessonData}
            onPrevious={handlePrevious}
            onStepComplete={processPayment}
          />
        );
      case 'confirmation':
        return (
          <BookingSubmissionStatus
            isSubmitting={isSubmitting}
            isSubmitted={isSubmitted}
            submitError={submitError}
            onTryAgain={() => setCurrentStep('payment')}
            onStartOver={() => setCurrentStep('selectHockeyLesson')}
          >
            <HockeyLessonConfirmation
              bookingData={lessonData}
              ticketNumber={lessonData.barcodeId || ''}
            />
          </BookingSubmissionStatus>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-0 md:p-6">
      {/* Progress Indicator */}
      <ProgressIndicator steps={STEPS} currentStep={currentStep} />

      {/* Current Step Content */}
      <div className="bg-white rounded-lg shadow-lg p-2 md:p-6">
        {renderCurrentStep()}
      </div>

      {/* Debug: Show waiver data */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Debug Data:
          </h3>
          <pre className="text-xs text-gray-600 overflow-auto">
            {JSON.stringify(lessonData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

export default HockeyLessonFlow;
